import {
  COLORS,
  SayIcon,
  SecondaryTabs,
  Text,
} from "@asayinc/component-library";
import { Box, Drawer, Stack } from "@mui/material";
import debounce from "lodash.debounce";
import { useState } from "react";

import { EmptyState } from "src/components/molecules/EmptyState";
import { SearchInputField } from "src/components/molecules/SearchInputField";
import { SearchResults } from "src/components/molecules/SearchResults";
import { JobSearchResults } from "src/components/molecules/JobSearchResults";
import {
  useSearchSecuritiesQuery,
  useSearchJobNumberQuery,
} from "src/store/globalSearch";
import { useCurrentUserQuery } from "src/store/user";

interface ISearchPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

type SearchTabs = "all" | "issuers" | "jobs";

export function SearchPanel({ isOpen, onClose }: ISearchPanelProps) {
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<SearchTabs>("all");
  const { data: currentUser } = useCurrentUserQuery();

  // Check user roles for filtering search results
  const isTabulator =
    currentUser?.tabulators && currentUser.tabulators.length > 0;
  const isClaimsAdministrator =
    currentUser?.claimsAdministrators &&
    currentUser.claimsAdministrators.length > 0;
  const isSolicitor =
    currentUser?.solicitors && currentUser.solicitors.length > 0;

  const debouncedSetSearchTerm = debounce(
    (term: string) => setSearchTerm(term),
    300
  );
  const { data: securitiesSearch, isFetching: isSecuritiesFetching } =
    useSearchSecuritiesQuery({ search: searchTerm }, { skip: !searchTerm });
  const { data: jobNumbersSearch } = useSearchJobNumberQuery(
    { search: searchTerm },
    { skip: !searchTerm }
  );

  const handleOnSearchChange = (value: string) => {
    debouncedSetSearchTerm(value);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as SearchTabs);
  };

  const handleOnClose = () => {
    setSearchTerm(null);
    onClose();
  };

  const matchedSecurities = securitiesSearch?.tickers.concat(
    securitiesSearch?.issuerNames
  );
  const filteredSecurities = matchedSecurities?.filter(
    (security, index, array) =>
      array.findIndex((compare) => compare.id === security.id) === index
  );

  const hasJobResults =
    (jobNumbersSearch?.proxyEvents?.length ?? 0) > 0 ||
    (jobNumbersSearch?.noboEvents?.length ?? 0) > 0 ||
    (jobNumbersSearch?.shareRangeAnalysisEvents?.length ?? 0) > 0 ||
    (jobNumbersSearch?.locationAnalysisEvents?.length ?? 0) > 0 ||
    (jobNumbersSearch?.bankruptcyEvents?.length ?? 0) > 0 ||
    (jobNumbersSearch?.classActionEvents?.length ?? 0) > 0;

  return (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={handleOnClose}
      ModalProps={{
        disablePortal: true,
        sx: {
          position: "absolute",
          display: "flex",
        },
      }}
      PaperProps={{
        sx: {
          m: 4,
          width: "640px",
          maxWidth: "640px",
          borderRadius: 2,
          flexGrow: 1,
          p: 8,
          position: "relative",
          height: "auto",
        },
      }}
    >
      <SearchInputField onChange={handleOnSearchChange} sx={{ mb: 6 }} />
      <Stack m={"0 auto"}>
        <SecondaryTabs
          sx={{ mb: 6 }}
          tabs={[
            {
              label: "All",
              value: "all",
              size: "small",
            },
            {
              label: "Issuers",
              value: "issuers",
              icon:
                activeTab === "issuers" ? (
                  <Box sx={{ display: "flex", mr: 3 }}>
                    <SayIcon name="IssuerFill" color="#1968FF" />
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", mr: 3 }}>
                    <SayIcon name="IssuerOutline" />
                  </Box>
                ),
              size: "small",
            },
            {
              label: "Jobs",
              value: "jobs",
              icon:
                activeTab === "jobs" ? (
                  <Box sx={{ display: "flex", mr: 3 }}>
                    <SayIcon name="DocumentFill" color="#1968FF" />
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", mr: 3 }}>
                    <SayIcon name="DocumentOutline" />
                  </Box>
                ),
              size: "small",
            },
          ]}
          activeTab={activeTab}
          clickHandler={handleTabChange}
        />
      </Stack>
      {!searchTerm && <EmptyState isGray={true} />}
      {searchTerm &&
        ((securitiesSearch?.tickers?.length ?? 0) === 0 &&
        (securitiesSearch?.issuerNames?.length ?? 0) === 0 &&
        !hasJobResults ? (
          <EmptyState
            showNoResults={true}
            title="No results found"
            subtitle="Try adjusting your search or filter to find what you're looking for"
          />
        ) : (
          <Box sx={{ mx: -8, px: 8, overflowY: "auto" }}>
            {(activeTab === "all" || activeTab === "issuers") && (
              <SearchResults
                isLoading={isSecuritiesFetching}
                onClose={handleOnClose}
                options={filteredSecurities}
                leftHeader="Ticker"
                rightHeader="Issuer"
                toCustomer={false}
              />
            )}
            {(activeTab === "all" || activeTab === "jobs") && (
              <Box
                sx={{
                  mt:
                    activeTab === "all" &&
                    filteredSecurities &&
                    filteredSecurities?.length > 0
                      ? 9
                      : 0,
                }}
              >
                <JobSearchResults
                  jobResults={jobNumbersSearch}
                  onClose={handleOnClose}
                  isTabulator={isTabulator}
                  isSolicitor={isSolicitor}
                  isClaimsAdministrator={isClaimsAdministrator}
                />
              </Box>
            )}
            {activeTab === "all" && (
              <Box sx={{ mt: 9, textAlign: "center" }}>
                <Text variant="body3" sx={{ color: COLORS.P_MED_EMPHASIS }}>
                  Can&apos;t find what you&apos;re looking for?
                </Text>
                <Text variant="body3" sx={{ color: COLORS.P_MED_EMPHASIS }}>
                  Try typing the full company name or ticker.
                </Text>
                <Text variant="body3" sx={{ color: COLORS.P_MED_EMPHASIS }}>
                  You can also adjust the filters on the top to narrow down your
                  search.
                </Text>
              </Box>
            )}
            {activeTab === "issuers" && (
              <Box sx={{ mt: 9, textAlign: "center" }}>
                <Text variant="body3" sx={{ color: COLORS.P_MED_EMPHASIS }}>
                  If you don&apos;t see what you&apos;re looking for,
                </Text>
                <Text variant="body3" sx={{ color: COLORS.P_MED_EMPHASIS }}>
                  try typing the full company name or ticker.
                </Text>
              </Box>
            )}
            {activeTab === "jobs" && (
              <Box sx={{ mt: 9, textAlign: "center" }}>
                <Text variant="body3" sx={{ color: COLORS.P_MED_EMPHASIS }}>
                  If you don&apos;t see what you&apos;re looking for,
                </Text>
                <Text variant="body3" sx={{ color: COLORS.P_MED_EMPHASIS }}>
                  try typing the full job number (e.g., PE29393, BE123, CLA456).
                </Text>
              </Box>
            )}
          </Box>
        ))}
    </Drawer>
  );
}
